/**
 * losi-spaces-tool-routing
 *
 * Shared action map for compact Losi Spaces platform tools.
 *
 * Primary responsibilities:
 * - Map grouped tool actions to legacy Spaces function ids
 * - Resolve routed function ids from tool arguments
 *
 * Dependencies:
 * - None
 */

export const LOSI_SPACES_TOOL_ACTIONS = {
  spaceTool: {
    list: 'listSpaces',
    create: 'createSpace',
    update: 'updateSpace',
    archive: 'archiveSpace'
  },
  spaceTaskTool: {
    list: 'listSpaceTasks',
    get: 'getSpaceTask',
    create: 'createSpaceTask',
    update: 'updateSpaceTask',
    delete: 'deleteSpaceTask',
    comment: 'createTaskComment',
    list_subtasks: 'listTaskSubtasks',
    create_subtask: 'createTaskSubtask',
    update_subtask: 'updateTaskSubtask',
    delete_subtask: 'deleteTaskSubtask',
    list_checklist_items: 'listTaskChecklistItems',
    create_checklist_item: 'createTaskChecklistItem',
    update_checklist_item: 'updateTaskChecklistItem',
    delete_checklist_item: 'deleteTaskChecklistItem',
    list_notes: 'listTaskNotes',
    delete_note: 'deleteTaskNote',
    list_linked_items: 'listTaskLinkedItems',
    search_attachable_items: 'searchTaskAttachableItems',
    attach_linked_item: 'attachTaskLinkedItem',
    remove_linked_item: 'removeTaskLinkedItem'
  },
  spaceNoteTool: {
    list: 'listSpaceNotes',
    get: 'getSpaceNote',
    create: 'createSpaceNote',
    update: 'updateSpaceNote',
    delete: 'deleteSpaceNote'
  },
  spaceDesignTool: {
    list: 'listSpaceDesigns',
    get: 'getSpaceDesign',
    create: 'createSpaceDesign',
    update: 'updateSpaceDesign',
    delete: 'deleteSpaceDesign',
    apply_scene_mutation: 'applySpaceDesignSceneMutation'
  },
  spaceSheetTool: {
    list: 'listSpaceSheets',
    get: 'getSpaceSheet',
    create: 'createSpaceSheet',
    update: 'updateSpaceSheet',
    delete: 'deleteSpaceSheet',
    replace_workbook: 'replaceSpaceSheetWorkbook',
    update_range: 'applySpaceSheetRangeUpdate',
    fill_range: 'applySpaceSheetRangeFill',
    transfer_range: 'applySpaceSheetRangeTransfer',
    update_structure: 'applySpaceSheetStructureUpdate',
    update_dimensions: 'applySpaceSheetDimensionUpdate',
    update_visibility: 'applySpaceSheetVisibilityUpdate'
  },
  spaceFolderTool: {
    list: 'listSpaceFolders',
    get: 'getSpaceFolder',
    create: 'createSpaceFolder',
    update: 'updateSpaceFolder'
  },
  spaceSprintTool: {
    list: 'listSpaceSprints',
    get: 'getSpaceSprint',
    create: 'createSpaceSprint',
    update: 'updateSpaceSprint'
  },
  spaceCalendarTool: {
    list_space_events: 'listSpaceEvents',
    get_space_event: 'getSpaceEvent',
    list_managed_calendars: 'listManagedCalendars',
    list_managed_events: 'listManagedCalendarEvents',
    create_managed_event: 'createManagedCalendarEvent',
    update_managed_event: 'updateManagedCalendarEvent',
    delete_managed_event: 'deleteManagedCalendarEvent',
    check_managed_availability: 'checkManagedCalendarAvailability',
    create_space_event: 'createSpaceEvent',
    update_space_event: 'updateSpaceEvent',
    delete_space_event: 'deleteSpaceEvent',
    archive_space_event: 'deleteSpaceEvent'
  },
  spaceContextTool: {
    list_files: 'listSpaceFiles',
    get_file: 'getSpaceFile',
    search_files: 'searchSpaceFiles',
    attach_file: 'attachSpaceFile',
    update_file: 'updateSpaceFile',
    detach_file: 'detachSpaceFile',
    list_collections: 'listSpaceCollections',
    get_collection: 'getSpaceCollection',
    attach_collection: 'attachSpaceCollection',
    update_collection: 'updateSpaceCollection',
    detach_collection: 'detachSpaceCollection',
    list_reference_memories: 'listSpaceReferenceMemories',
    get_reference_memory: 'getSpaceReferenceMemory',
    create_reference_memory: 'createSpaceReferenceMemory',
    update_reference_memory: 'updateSpaceReferenceMemory',
    archive_reference_memory: 'archiveSpaceReferenceMemory'
  },
  spaceMemberTool: {
    list: 'listSpaceMembers',
    directory: 'listSpaceMemberDirectory',
    add: 'addSpaceMember',
    update_role: 'updateSpaceMemberRole',
    remove: 'removeSpaceMember',
    invite: 'inviteToSpace'
  },
  spaceChatTool: {
    list: 'listSpaceChats',
    get: 'getSpaceChat',
    link: 'linkSpaceChat',
    move: 'moveSpaceChat',
    unlink: 'unlinkSpaceChat'
  },
  spaceConversationTool: {
    list: 'listSpaceConversations',
    start: 'startSpaceConversation',
    get: 'getSpaceConversation',
    update: 'updateSpaceConversation',
    archive: 'archiveSpaceConversation',
    duplicate: 'duplicateSpaceConversation',
    list_messages: 'listSpaceConversationMessages',
    respond: 'respondSpaceConversation'
  },
  spaceWhiteboardTool: {
    list: 'listSpaceWhiteboards',
    get: 'getSpaceWhiteboard',
    create: 'createSpaceWhiteboard',
    update: 'updateSpaceWhiteboard',
    delete: 'deleteSpaceWhiteboard'
  }
} as const

export const LOSI_SPACES_ROUTER_TOOL_LABELS: Record<string, string> = {
  spaceTool: 'Spaces',
  spaceTaskTool: 'Space tasks',
  spaceNoteTool: 'Space notes',
  spaceDesignTool: 'Space designs',
  spaceSheetTool: 'Space sheets',
  spaceFolderTool: 'Space folders',
  spaceSprintTool: 'Space sprints',
  spaceCalendarTool: 'Space calendars',
  spaceContextTool: 'Space context',
  spaceMemberTool: 'Space members',
  spaceChatTool: 'Space chats',
  spaceConversationTool: 'Space conversations',
  spaceWhiteboardTool: 'Space whiteboards'
}

type LosiSpacesToolActions = typeof LOSI_SPACES_TOOL_ACTIONS
export type LosiSpacesRouterFunctionId = keyof LosiSpacesToolActions

// --------------

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  return value as Record<string, unknown>
}

// --------------

function asNonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

// --------------

export function isLosiSpacesRouterFunctionId(value: string): value is LosiSpacesRouterFunctionId {
  return value in LOSI_SPACES_TOOL_ACTIONS
}

// --------------

export function getLosiSpacesRouterAction(args: unknown): string | null {
  const record = asRecord(args)
  if (!record) return null

  const directAction = asNonEmptyString(record.action)
  if (directAction) return directAction

  const routedAction = asNonEmptyString(record.routerAction)
  if (routedAction) return routedAction

  const result = asRecord(record.result)
  const resultAction = asNonEmptyString(result?.routerAction) ?? asNonEmptyString(result?.action)
  if (resultAction) return resultAction

  const parameters = asRecord(record.parameters)
  return parameters ? asNonEmptyString(parameters.action) : null
}

/**
 * Models often call spaceNoteTool / spaceTaskTool with create/update-shaped
 * params but omit `action`. Infer only when the shape is unambiguous — never
 * invent an action when both create and update are plausible.
 */
export function inferLosiSpacesRouterAction(
  routerFunctionId: string,
  args: unknown
): string | null {
  if (!isLosiSpacesRouterFunctionId(routerFunctionId)) {
    return null
  }

  const record = asRecord(args)
  if (!record) return null

  const parameters = asRecord(record.parameters)
  const requestBody = asRecord(record.requestBody)
  const flat: Record<string, unknown> = {
    ...(requestBody ?? {}),
    ...(parameters ?? {}),
    ...record
  }

  const has = (key: string) => {
    const value = flat[key]
    if (value === null || value === undefined) return false
    if (typeof value === 'string') return value.trim().length > 0
    return true
  }

  const actionMap = LOSI_SPACES_TOOL_ACTIONS[routerFunctionId] as Record<string, string>
  const supports = (action: string) => Boolean(actionMap[action])

  if (routerFunctionId === 'spaceNoteTool') {
    if (has('noteId') && (has('title') || has('content') || has('sharing') || has('folderId'))) {
      return supports('update') ? 'update' : null
    }
    if (has('noteId') && supports('get')) {
      return 'get'
    }
    if (has('title') && has('spaceId') && supports('create')) {
      return 'create'
    }
    if (has('spaceId') && supports('list')) {
      return 'list'
    }
    return null
  }

  if (routerFunctionId === 'spaceTaskTool') {
    if (has('taskId') && (has('title') || has('status') || has('description') || has('assigneeId'))) {
      return supports('update') ? 'update' : null
    }
    if (has('taskId') && supports('get')) {
      return 'get'
    }
    if (has('title') && has('spaceId') && supports('create')) {
      return 'create'
    }
    if (has('spaceId') && supports('list')) {
      return 'list'
    }
    return null
  }

  return null
}

/** Resolve explicit action, then unambiguous param-shape inference. */
export function resolveLosiSpacesRouterAction(
  routerFunctionId: string,
  args: unknown
): string | null {
  return getLosiSpacesRouterAction(args) ?? inferLosiSpacesRouterAction(routerFunctionId, args)
}

// --------------

const normalizeForFuzzyMatch = (value: string) =>
  value.toLowerCase().replace(/[^a-z]/g, '')

// Common synonyms models use for the canonical action verbs. Mapping these
// prevents "Unsupported <tool> action" failures (e.g. the model calling
// spaceNoteTool with `append`/`write` when it means `update`). Only applied
// when the raw action is not already a valid key for the tool.
const LOSI_SPACES_ACTION_ALIASES: Record<string, string> = {
  append: 'update',
  write: 'update',
  edit: 'update',
  modify: 'update',
  change: 'update',
  save: 'update',
  upsert: 'update',
  patch: 'update',
  assign: 'update',
  reassign: 'update',
  set_assignee: 'update',
  setassignee: 'update',
  add: 'create',
  new: 'create',
  make: 'create',
  insert: 'create',
  draft: 'create',
  create_note: 'create',
  createnote: 'create',
  write_note: 'create',
  writenote: 'create',
  add_note: 'create',
  addnote: 'create',
  create_task: 'create',
  createtask: 'create',
  add_task: 'create',
  addtask: 'create',
  read: 'get',
  fetch: 'get',
  view: 'get',
  show: 'get',
  open: 'get',
  remove: 'delete',
  destroy: 'delete',
  archive: 'delete',
  all: 'list',
  index: 'list',
  search: 'list'
}

// Tool-specific overrides — e.g. spaceMemberTool owns real `add`/`invite`, so
// the global `add → create` alias must not win there.
const LOSI_SPACES_TOOL_ACTION_ALIASES: Partial<
  Record<LosiSpacesRouterFunctionId, Record<string, string>>
> = {
  spaceMemberTool: {
    create: 'add',
    add_member: 'add',
    addmember: 'add',
    add_user: 'add',
    adduser: 'add',
    invite_member: 'invite',
    invitemember: 'invite',
    invite_user: 'invite',
    inviteuser: 'invite'
  },
  spaceTaskTool: {
    assign: 'update',
    reassign: 'update',
    set_assignee: 'update',
    setassignee: 'update'
  },
  spaceNoteTool: {
    create_note: 'create',
    createnote: 'create',
    // Global alias maps `write` → update; for notes models almost always
    // mean create a new note (update uses edit/append/update).
    write: 'create',
    write_note: 'create',
    writenote: 'create',
    add_note: 'create',
    addnote: 'create',
    upsert: 'update'
  }
}

export function getLosiSpacesLegacyFunctionId(
  routerFunctionId: string,
  action: unknown
): string | null {
  if (!isLosiSpacesRouterFunctionId(routerFunctionId)) {
    return null
  }

  const normalizedAction = asNonEmptyString(action)
  if (!normalizedAction) {
    return null
  }

  const actionMap = LOSI_SPACES_TOOL_ACTIONS[routerFunctionId] as Record<string, string>
  const direct = actionMap[normalizedAction]
  if (direct) {
    return direct
  }

  const actionKey = normalizedAction.toLowerCase()
  const toolAliases = LOSI_SPACES_TOOL_ACTION_ALIASES[routerFunctionId]
  const toolAliased = toolAliases?.[actionKey]
  if (toolAliased && actionMap[toolAliased]) {
    return actionMap[toolAliased]
  }

  // Fall back to an aliased canonical verb if the tool supports it.
  const aliased = LOSI_SPACES_ACTION_ALIASES[actionKey]
  if (aliased && actionMap[aliased]) {
    return actionMap[aliased]
  }

  // Fuzzy: createNote / create_note / CREATE-NOTE → create when that key exists.
  const fuzzyAction = normalizeForFuzzyMatch(normalizedAction)
  if (fuzzyAction) {
    const fuzzyKey = Object.keys(actionMap).find(
      key => normalizeForFuzzyMatch(key) === fuzzyAction
    )
    if (fuzzyKey) {
      return actionMap[fuzzyKey]
    }

    const fuzzyAlias = Object.entries(LOSI_SPACES_ACTION_ALIASES).find(
      ([from]) => normalizeForFuzzyMatch(from) === fuzzyAction
    )?.[1]
    if (fuzzyAlias && actionMap[fuzzyAlias]) {
      return actionMap[fuzzyAlias]
    }

    const fuzzyToolAlias = toolAliases
      ? Object.entries(toolAliases).find(
          ([from]) => normalizeForFuzzyMatch(from) === fuzzyAction
        )?.[1]
      : null
    if (fuzzyToolAlias && actionMap[fuzzyToolAlias]) {
      return actionMap[fuzzyToolAlias]
    }
  }

  return null
}

// --------------

export function getRoutedLosiSpacesFunctionId(functionId: string, args: unknown): string | null {
  return getLosiSpacesLegacyFunctionId(functionId, getLosiSpacesRouterAction(args))
}

// --------------

export function getRoutedLosiSpacesToolKey(key: string, args: unknown): string | null {
  if (!key.startsWith('losiSpaces__')) {
    return null
  }

  const record = asRecord(args)
  const result = asRecord(record?.result)
  const directRoutedFunctionId =
    asNonEmptyString(record?.routedFunctionId) ?? asNonEmptyString(result?.routedFunctionId)
  if (directRoutedFunctionId) {
    return `losiSpaces__${directRoutedFunctionId}`
  }

  const functionId = key.slice('losiSpaces__'.length)
  const routedFunctionId = getRoutedLosiSpacesFunctionId(functionId, args)
  return routedFunctionId ? `losiSpaces__${routedFunctionId}` : null
}

/**
 * Models occasionally invent a per-action function name (e.g.
 * `losiSpaceTaskTool__attach`) instead of calling the grouped tool with an
 * `action` param. Rather than a bare "not found", suggest the real grouped
 * tool + action so the model can self-correct on its next attempt.
 */
export function suggestLosiSpacesFunctionName(functionName: string): string | null {
  const separatorIndex = functionName.indexOf('__')
  const rawToolPart = separatorIndex >= 0 ? functionName.slice(0, separatorIndex) : functionName
  const rawActionPart = separatorIndex >= 0 ? functionName.slice(separatorIndex + 2) : null

  let keyCandidate = normalizeForFuzzyMatch(rawToolPart)
  if (keyCandidate.startsWith('losi')) {
    keyCandidate = keyCandidate.slice('losi'.length)
  }
  if (!keyCandidate) {
    return null
  }

  const groupedKeys = Object.keys(LOSI_SPACES_TOOL_ACTIONS) as LosiSpacesRouterFunctionId[]
  // Prefer a prefix match (the real tool name, followed by invented batch/
  // placeholder/pagination suffixes like `_batch1` or `_placeholder_skip`)
  // before falling back to a looser substring match for typo'd tool names.
  const matchedKey =
    groupedKeys.find((key) => keyCandidate.startsWith(normalizeForFuzzyMatch(key))) ??
    groupedKeys.find((key) => {
      const normalizedKey = normalizeForFuzzyMatch(key)
      return normalizedKey.includes(keyCandidate) || keyCandidate.includes(normalizedKey)
    })
  if (!matchedKey) {
    return null
  }

  const normalizedKey = normalizeForFuzzyMatch(matchedKey)
  const trailingFromToolPart = keyCandidate.startsWith(normalizedKey)
    ? keyCandidate.slice(normalizedKey.length)
    : ''
  const normalizedActionHint = normalizeForFuzzyMatch(
    `${trailingFromToolPart}${rawActionPart ?? ''}`
  )

  const actionKeys = Object.keys(LOSI_SPACES_TOOL_ACTIONS[matchedKey] as Record<string, string>)
  const matchedAction = normalizedActionHint
    ? actionKeys.find((actionKey) => {
        const normalizedActionKey = normalizeForFuzzyMatch(actionKey)
        return (
          normalizedActionKey.startsWith(normalizedActionHint) ||
          normalizedActionHint.startsWith(normalizedActionKey)
        )
      })
    : null

  return matchedAction
    ? `losiSpaces__${matchedKey} with { action: "${matchedAction}" }`
    : `losiSpaces__${matchedKey} with one of these actions: ${actionKeys.join(', ')}`
}
